<template>
  <div
    class="fixed top-0 left-0 right-0 bottom-0 inset-0 bg-black/50 flex sm:items-center justify-center p-4 md:p-8 z-50 animate-[fadeIn_0.3s]"
  >
    <div
      class="bg-white sm:rounded-2xl shadow-xl w-full sm:max-w-md p-6 relative max-h-full overflow-y-auto"
      @click.stop
    >
      <button
        @click="closeModal"
        class="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl leading-none p-2 cursor-pointer"
      >
        &times;
      </button>
      <h2 class="h2">
        {{ mode === "login" ? "Welcome back" : "Create an account" }}
      </h2>
      <p class="text-sm mb-6">
        {{
          mode === "login"
            ? "Sign in to your account"
            : "Enter your details to get started"
        }}
      </p>
      <div
        v-if="mode !== 'login' && isSignedUp"
        class="flex flex-col items-center"
      >
        <CheckSuccess class="text-green-700 h-24 w-24 mb-4" />
        <h3>Your Account Has Been Created</h3>
      </div>
      <div v-else-if="isOTPRequired" class="">
        <form @submit.prevent="handleSubmit">
          <p>Please confirm your email</p>
          <p>We have sent an one time passcode to your email</p>
          <FormInput
            label="Enter the OTP"
            placeholder="••••••••"
            v-model="form.otp"
            :error="errors?.otp"
            autofocus
            type="number"
          />
          <button
            type="submit"
            class="btn btn-block btn-primary"
            :disabled="isLoading"
            :class="{ 'btn-working': isLoading }"
          >
            {{ "Submit" }}
          </button>
        </form>
      </div>
      <form @submit.prevent="handleSubmit" v-else>
        <fieldset :disabled="isLoading">
          <FormInput
            label="Email"
            placeholder="john@example.com"
            v-model="form.email"
            :error="errors?.email"
            type="email"
            autofocus
          />
          <FormInput
            label="Name"
            placeholder="John Doe"
            v-model="form.name"
            :error="errors?.name"
            v-if="mode !== 'login'"
          />
          <FormInput
            type="password"
            label="Password"
            placeholder="••••••••"
            v-model="form.password"
            :error="errors?.password"
          />
          <FormInput
            type="password"
            label="Confirm Password"
            placeholder="••••••••"
            v-model="form.confirmPassword"
            v-if="mode !== 'login'"
            :error="errors?.confirmPassword"
          />
        </fieldset>
        <button
          type="submit"
          class="btn btn-block btn-primary"
          :disabled="isLoading"
          :class="{ 'btn-working': isLoading }"
        >
          {{ mode === "login" ? "Sign in" : "Sign up" }}
        </button>
      </form>
      <p class="text-sm text-center text-gray-600 mt-8">
        <span v-if="mode === 'login'">
          Don't have an account?
          <button type="button" @click.prevent="toggleMode" class="a">
            Sign up
          </button>
        </span>
        <span v-else>
          Already have an account?
          <button type="button" @click.prevent="toggleMode" class="a">
            Sign in
          </button>
        </span>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, watch } from "vue";
import CheckSuccess from "@/lib/components/CheckSuccess.vue";
import { FormInput } from "@/lib/components/forms";
import validate from "@/lib/validation";
import { useLoginState } from "@/pages/store";

type Mode = "login" | "signup";

const emit = defineEmits(["close"]);
const isLoading = ref(false);
const props = defineProps<{ xMode?: Mode }>();
const { user } = useLoginState();

const ERROR_MESSAGES = {
  rate_limited: "Too many attempts, please wait a minute",
  invalid_login: "Invalid email or password",
  invalid_user: "Invalid email or password",
  user_exists: "Email is already in use",
  invalid_email: "Email not allowed",
  unauthorized_signup: "Sign up not allowed",
};

watch(
  () => props.xMode,
  () => (mode.value = props.xMode ?? "signup"),
);

const form = reactive({
  name: "",
  email: "",
  password: "",
  confirmPassword: "",
  otp: "",
});
const mode = ref<Mode>(props.xMode ?? "signup");

const errors = ref<Partial<typeof form>>();
const isSignedUp = ref(false);
const isOTPRequired = ref(false);

const toggleMode = () => {
  isLoading.value = false;
  isOTPRequired.value = false;
  mode.value = mode.value === "login" ? "signup" : "login";
};

const closeModal = () => {
  emit("close");
};

const handleSubmit = async () => {
  const { $auth } = await import("@/api.client");
  const { hasError, errorMessages } = validate(
    {
      name: mode.value === "signup",
      email: [true, "email"],
      password: true,
      confirmPassword: mode.value === "signup",
    },
    form,
  );
  errors.value = errorMessages;
  if (hasError) return;

  if (mode.value === "login") {
    return login();
  }
  if (mode.value === "signup" && form.password !== form.confirmPassword) {
    errors.value = { confirmPassword: "Password did not match" };
    return;
  }
  if (mode.value === "signup") {
    isLoading.value = true;
    try {
      const { success, error } = await $auth.signup({
        name: form.name,
        email: form.email,
        password: form.password,
        otp: form.otp,
      });
      isLoading.value = false;
      if (error === "otp_required") {
        isOTPRequired.value = true;
        return;
      } else if (error) {
        errors.value = { email: ERROR_MESSAGES[error] ?? error };
        return;
      }
      if (success) login();
    } catch (err) {
      if (err instanceof Error && err.message === "user_exists") {
        errors.value = { email: "Email is already registered" };
      }
    }
  }

  async function login() {
    const { Toast } = await import("@/lib/toast.client");
    isLoading.value = true;
    const { success: loginSuccess, error } = await $auth.login({
      email: form.email,
      password: form.password,
    });
    isLoading.value = false;
    if (loginSuccess) {
      user.value = await $auth.getMe();
      Toast(`Welcome back ${user.value?.name}`, {
        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M12 19.2c-2.5 0-4.71-1.28-6-3.2c.03-2 4-3.1 6-3.1s5.97 1.1 6 3.1a7.23 7.23 0 0 1-6 3.2M12 5a3 3 0 0 1 3 3a3 3 0 0 1-3 3a3 3 0 0 1-3-3a3 3 0 0 1 3-3m0-3A10 10 0 0 0 2 12a10 10 0 0 0 10 10a10 10 0 0 0 10-10c0-5.53-4.5-10-10-10"/></svg>`,
      });
    } else if (error) {
      errors.value = { email: ERROR_MESSAGES[error] ?? error };
    }
  }
};
</script>
